/**
 * DeviceBreakdownSection.tsx
 *
 * Device breakdown (Mobile / Desktop / Tablet) for the Meta Lead Gen dashboard.
 * All data is pre-computed by deviceBreakdown.ts — this file is pure UI.
 *
 * Shows only real Meta data: Spend, Opt-ins, Cost per Opt-in, CTR by device.
 */

import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { branding } from "../../../../config/branding";
import type { DeviceDailyRow } from "../deviceBreakdown";

// ─────────────────────────────────────────────
// COLOR PALETTE
// ─────────────────────────────────────────────
const C = branding.colors;

const COLOR_IOS     = C.device?.ios     ?? "#3b82f6";  // blue  — iOS
const COLOR_ANDROID = C.device?.android ?? "#22c55e";  // green — Android
const COLOR_DESKTOP = C.chart[2];                      // accent — Desktop

// ─────────────────────────────────────────────
// SHARED CHART CONFIG
// ─────────────────────────────────────────────
const CHART_H = 220;

const TICK_STYLE = { fontSize: 10, fill: "currentColor", opacity: 0.5 } as const;
const AXIS_PROPS = { axisLine: false, tickLine: false, tick: TICK_STYLE } as const;

const GRID_PROPS = {
  strokeDasharray: "3 3",
  vertical: false,
  stroke: "currentColor",
  strokeOpacity: 0.08,
} as const;

// ─────────────────────────────────────────────
// CARD WRAPPER
// ─────────────────────────────────────────────
function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="mb-4 text-sm font-semibold text-foreground">{title}</p>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────
// LEGEND DOTS
// ─────────────────────────────────────────────
interface LegendItem { label: string; color: string; dashed?: boolean }

function LegendDots({ items }: { items: LegendItem[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
      {items.map(it => (
        <span key={it.label} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span
            className="inline-block w-4 h-[2px] rounded-full"
            style={{
              background: it.color,
              opacity: it.dashed ? 0.6 : 1,
              borderBottom: it.dashed ? `2px dashed ${it.color}` : undefined,
              background: it.dashed ? "transparent" : it.color,
            } as React.CSSProperties}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// LINE CONFIG TYPE
// ─────────────────────────────────────────────
interface LineConfig {
  dataKey: string;
  label: string;
  color: string;
  dashed?: boolean;
}

// ─────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────
function buildTooltip(formatter: (v: number) => string) {
  return function DeviceTooltip({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number | null; color: string }>;
    label?: string;
  }) {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg border border-border bg-popover/95 backdrop-blur-sm px-3 py-2 shadow-xl text-xs">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload.map(p => (
          <div key={p.name} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
            <span className="text-muted-foreground">{p.name}:</span>
            <span className="font-medium text-foreground">
              {p.value !== null ? formatter(p.value) : "N/A"}
            </span>
          </div>
        ))}
      </div>
    );
  };
}

// ─────────────────────────────────────────────
// GENERIC DEVICE LINE CHART
// ─────────────────────────────────────────────
interface DeviceLineChartProps {
  data: DeviceDailyRow[];
  lines: LineConfig[];
  formatter: (v: number) => string;
  yAxisFormatter: (v: number) => string;
}

function DeviceLineChart({ data, lines, formatter, yAxisFormatter }: DeviceLineChartProps) {
  const TooltipContent = useMemo(() => buildTooltip(formatter), [formatter]);
  const interval = Math.max(0, Math.ceil(data.length / 8) - 1);

  return (
    <>
      <LegendDots items={lines.map(l => ({ label: l.label, color: l.color, dashed: l.dashed }))} />
      <ResponsiveContainer width="100%" height={CHART_H}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="date" {...AXIS_PROPS} dy={6} interval={interval} />
          <YAxis {...AXIS_PROPS} tickFormatter={yAxisFormatter} width={52} />
          <Tooltip content={<TooltipContent />} cursor={{ stroke: "currentColor", strokeOpacity: 0.1 }} />
          {lines.map(l => (
            <Line
              key={l.dataKey}
              type="monotone"
              dataKey={l.dataKey}
              name={l.label}
              stroke={l.color}
              strokeWidth={l.dashed ? 1.5 : 2}
              strokeDasharray={l.dashed ? "5 3" : undefined}
              strokeOpacity={l.dashed ? 0.7 : 1}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

// ─────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────
const fmtPct    = (v: number) => `${v.toFixed(1)}%`;
const fmtCur    = (v: number) => `$${v.toFixed(2)}`;
const fmtCurK   = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
const fmtCount  = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));
const fmtPctAxis = (v: number) => `${v.toFixed(0)}%`;
const fmtCurAxis = (v: number) => `$${v.toFixed(0)}`;

// ─────────────────────────────────────────────
// LINE DEFINITIONS
// ─────────────────────────────────────────────
const LINES_SPEND: LineConfig[] = [
  { dataKey: "ios_spend",     label: "iOS",     color: COLOR_IOS },
  { dataKey: "android_spend", label: "Android", color: COLOR_ANDROID },
  { dataKey: "desktop_spend", label: "Desktop", color: COLOR_DESKTOP, dashed: true },
];

const LINES_OPTINS: LineConfig[] = [
  { dataKey: "ios_optins",     label: "iOS",     color: COLOR_IOS },
  { dataKey: "android_optins", label: "Android", color: COLOR_ANDROID },
  { dataKey: "desktop_optins", label: "Desktop", color: COLOR_DESKTOP, dashed: true },
];

const LINES_CPO: LineConfig[] = [
  { dataKey: "ios_cpo",     label: "iOS",     color: COLOR_IOS },
  { dataKey: "android_cpo", label: "Android", color: COLOR_ANDROID },
  { dataKey: "desktop_cpo", label: "Desktop", color: COLOR_DESKTOP, dashed: true },
];

const LINES_CTR: LineConfig[] = [
  { dataKey: "ios_ctr",     label: "iOS",     color: COLOR_IOS },
  { dataKey: "android_ctr", label: "Android", color: COLOR_ANDROID },
  { dataKey: "desktop_ctr", label: "Desktop", color: COLOR_DESKTOP, dashed: true },
];

// ─────────────────────────────────────────────
// MAIN SECTION
// ─────────────────────────────────────────────
interface DeviceBreakdownSectionProps {
  dailyData: DeviceDailyRow[];
}

export function DeviceBreakdownSection({ dailyData }: DeviceBreakdownSectionProps) {
  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-2">
          Device Breakdown
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Row 1 — Spend | Opt-ins */}
      <div className="grid grid-cols-2 gap-4">
        <ChartCard title="Spend by Device">
          <DeviceLineChart
            data={dailyData}
            lines={LINES_SPEND}
            formatter={fmtCurK}
            yAxisFormatter={fmtCurK}
          />
        </ChartCard>

        <ChartCard title="Opt-ins by Device">
          <DeviceLineChart
            data={dailyData}
            lines={LINES_OPTINS}
            formatter={fmtCount}
            yAxisFormatter={fmtCount}
          />
        </ChartCard>
      </div>

      {/* Row 2 — Cost per Opt-in | CTR */}
      <div className="grid grid-cols-2 gap-4">
        <ChartCard title="Cost per Opt-in by Device">
          <DeviceLineChart
            data={dailyData}
            lines={LINES_CPO}
            formatter={fmtCur}
            yAxisFormatter={fmtCurAxis}
          />
        </ChartCard>

        <ChartCard title="CTR by Device">
          <DeviceLineChart
            data={dailyData}
            lines={LINES_CTR}
            formatter={fmtPct}
            yAxisFormatter={fmtPctAxis}
          />
        </ChartCard>
      </div>
    </div>
  );
}
